import React, { useState } from 'react';
import Navbar from '../../components/navbar/navbar';
import Footer from '../../components/footer/index';
import { useDarkMode } from '../../components/darkmode/DarkModeContext'; // Import the context
import Diskon from '../../components/layanan/it_speaker/paket';
import { Volume2, Wifi, Server, Headphones, Bell, CheckCircle, Zap, ArrowRight, MessageCircle } from 'lucide-react';

const IPSpeakerPage = () => {
  // Remove local dark mode state and use context instead
  const { isDarkMode } = useDarkMode();

  const ipSpeakerFeatures = [
    {
      icon: <Volume2 className="w-8 h-8" />,
      title: "Kualitas Suara Jernih",
      description: "Suara berkualitas tinggi dengan teknologi audio digital untuk pengumuman yang jelas",
      color: "bg-blue-500"
    },
    {
      icon: <Wifi className="w-8 h-8" />,
      title: "Jaringan IP Terintegrasi",
      description: "Memanfaatkan infrastruktur jaringan IP yang sudah ada tanpa kabel audio tambahan",
      color: "bg-purple-500"
    },
    {
      icon: <Server className="w-8 h-8" />,
      title: "Kontrol Terpusat",
      description: "Kelola dan kendalikan semua speaker dari satu antarmuka pusat",
      color: "bg-green-500"
    },
    {
      icon: <Zap className="w-8 h-8" />,
      title: "Instalasi Mudah",
      description: "Pemasangan sederhana dengan koneksi jaringan dan daya melalui PoE (Power over Ethernet)",
      color: "bg-yellow-500"
    },
    {
      icon: <Headphones className="w-8 h-8" />,
      title: "Zona Audio Fleksibel",
      description: "Membuat zona audio berbeda untuk area tertentu dengan pengaturan khusus",
      color: "bg-red-500"
    },
    {
      icon: <Bell className="w-8 h-8" />,
      title: "Sistem Peringatan Darurat",
      description: "Fitur prioritas untuk pengumuman darurat yang mengesampingkan semua audio lainnya",
      color: "bg-indigo-500"
    }
  ];

  const useCases = [
    {
      title: "Pengumuman Perusahaan",
      description: "Sistem pengumuman untuk komunikasi internal di seluruh area perusahaan",
      image: "/image/layanan/office-announcement.png",
      benefits: ["Pengumuman serentak", "Zona spesifik", "Jadwal otomatis"]
    },
    {
      title: "Sistem Peringatan Darurat",
      description: "Notifikasi darurat untuk evakuasi atau situasi kritis lainnya",
      image: "/image/layanan/emergency-system.png",
      benefits: ["Prioritas tinggi", "Override otomatis", "Peringatan multi-bahasa"]
    },
    {
      title: "Latar Belakang Musik",
      description: "Memutar musik latar di area umum atau ruang tunggu",
      image: "/image/layanan/background-music.png",
      benefits: ["Multiple channel", "Kontrol volume per zona", "Jadwal pemutaran"]
    },
    {
      title: "Panduan Pengunjung",
      description: "Sistem informasi dan panduan untuk pengunjung di fasilitas publik",
      image: "/image/layanan/visitor-guidance.png",
      benefits: ["Panduan audio", "Informasi titik penting", "Multi-bahasa"]
    }
  ];

  const implementationSteps = [
    {
      step: "1",
      title: "Konsultasi & Assessment",
      description: "Analisis kebutuhan dan survei lokasi untuk merancang solusi terbaik"
    },
    {
      step: "2",
      title: "Desain Sistem",
      description: "Perancangan sistem sesuai layout dan kebutuhan akustik ruangan"
    },
    {
      step: "3",
      title: "Instalasi Hardware",
      description: "Pemasangan speaker, kabel, dan perangkat pendukung lainnya"
    },
    {
      step: "4",
      title: "Konfigurasi Software",
      description: "Setup sistem kontrol dan integrasi dengan jaringan existing"
    },
    {
      step: "5",
      title: "Testing & Kalibrasi",
      description: "Pengujian seluruh sistem dan kalibrasi audio untuk hasil optimal"
    },
    {
      step: "6",
      title: "Pelatihan & Serah Terima",
      description: "Pelatihan penggunaan dan dokumentasi sistem untuk tim Anda"
    }
  ];

  const packages = [
    {
      id: 1,
      name: "Paket Dasar",
      popular: false,
      color: "from-blue-500 to-blue-700",
      coverage: "Hingga 10 zona",
      speakers: "5-10 unit",
      features: [
        "Sistem pengumuman dasar",
        "Kontrol via software",
        "Dukungan PoE",
        "Garansi 1 tahun",
        "Installasi dasar"
      ]
    },
    {
      id: 2,
      name: "Paket Bisnis",
      popular: true,
      color: "from-green-500 to-green-700",
      coverage: "Hingga 25 zona",
      speakers: "11-25 unit",
      features: [
        "Semua fitur Paket Dasar",
        "Integrasi emergency system",
        "Kontrol multi-user",
        "Sistem backup",
        "Monitoring remote",
        "Garansi 2 tahun",
        "Training staf"
      ]
    },
    {
      id: 3,
      name: "Paket Enterprise",
      popular: false,
      color: "from-purple-500 to-purple-700",
      coverage: "Lebih dari 25 zona",
      speakers: "Custom jumlah unit",
      features: [
        "Semua fitur Paket Bisnis",
        "Integrasi dengan sistem keamanan",
        "API untuk kustomisasi",
        "Redundancy system",
        "Support 24/7",
        "Garansi 3 tahun",
        "Maintenance berkala"
      ]
    }
  ];

  // Fungsi untuk mengarahkan ke WhatsApp dengan pesan otomatis
  const contactViaWhatsApp = (packageName = "IP Speaker System") => {
    const phoneNumber = "6287778882022"; // Ganti dengan nomor WhatsApp perusahaan
    const message = `Halo, saya tertarik dengan layanan ${packageName}. Bisakah saya mendapatkan informasi lebih lanjut?`;
    const encodedMessage = encodeURIComponent(message);
    window.open(`https://wa.me/${phoneNumber}?text=${encodedMessage}`, '_blank');
  };

  return (
    <div className={`min-h-screen transition-colors duration-300 ${
      isDarkMode ? 'bg-gray-900' : 'bg-[#dfe4e0ff]'
    }`}>
      
      {/* Navigation */}
      <Navbar />
      
      {/* Hero Section */}
      <section
        className={`py-20 px-4 relative overflow-hidden ${
          isDarkMode ? 'bg-gray-900' : ''
        }`}
        style={{
          backgroundColor: isDarkMode ? '#111827' : '#dfe4e0ff',
          backgroundImage: isDarkMode
            ? "linear-gradient(rgba(0, 0, 0, 0.7), rgba(0, 0, 0, 0.7)), url('/image/ip-speaker-dark.jpg')"
            : "linear-gradient(rgba(255, 255, 255, 0.8), rgba(255, 255, 255, 0.8)), url('/image/ip-speaker.jpg')",
          backgroundSize: 'cover',
          backgroundPosition: 'center',
        }}
      >
        <div className="container mx-auto relative z-10">
          <div className="text-center mb-12">
            <div className={`inline-flex items-center gap-2 ${
              isDarkMode ? 'bg-green-900/30 text-green-400' : 'bg-green-100 text-[#1E9B4C]'
            } px-4 py-2 rounded-full text-sm font-medium mb-6`}>
              <Volume2 className="w-4 h-4" />
              IP Speaker System Solutions
            </div>

            <h1
              className={`text-4xl md:text-6xl font-bold mb-6 ${
                isDarkMode ? 'text-white' : 'text-gray-900'
              }`}
            >
              Penyediaan & Instalasi
              <span className="block text-transparent bg-clip-text bg-gradient-to-r from-[#1E9B4C] to-green-700">
                IP Speaker System
              </span>
            </h1>

            <p
              className={`text-xl max-w-3xl mx-auto mb-8 ${
                isDarkMode ? 'text-gray-300' : 'text-gray-600'
              }`}
            >
              Solusi sistem pengeras suara berbasis IP untuk kebutuhan komunikasi publik, 
              pengumuman, dan sistem peringatan darurat yang terintegrasi dengan jaringan perusahaan.
            </p>

            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <button 
                onClick={() => contactViaWhatsApp("IP Speaker System")}
                className="bg-[#1E9B4C] hover:bg-green-700 text-white font-semibold px-8 py-4 rounded-lg transition-colors duration-200 flex items-center justify-center gap-2"
              >
                <MessageCircle className="w-5 h-5" />
                Konsultasi Gratis
              </button>
            </div>
          </div>
        </div>
      </section>

       <div className="absolute inset-0">
        <img
          src="https://telnet.net.id/assets/bg.png"
          alt="Background pattern"
          className="w-full h-full object-cover opacity-20"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-transparent via-white/40 to-transparent dark:via-black/40"></div>
      </div>

      {/* Features Section */}
      <section className={`py-16 px-4 transition-colors duration-300 ${
        isDarkMode ? 'bg-gray-800' : 'bg-white'
      }`}>
        <div className="container mx-auto">
          <div className="text-center mb-12">
            <h2
              className={`text-3xl font-bold mb-4 ${
                isDarkMode ? 'text-white' : 'text-gray-900'
              }`}
            >
              Keunggulan Sistem IP Speaker
            </h2>
            <p
              className={`text-lg ${
                isDarkMode ? 'text-gray-300' : 'text-gray-600'
              }`}
            >
              Teknologi terkini untuk sistem pengumuman dan komunikasi yang andal
            </p>
            <div className="w-24 h-1 bg-gradient-to-r from-[#1E9B4C] to-green-600 mx-auto rounded-full mt-4"></div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {ipSpeakerFeatures.map((feature, index) => (
              <div
                key={index}
                className={`
                  group p-6 rounded-xl shadow-lg overflow-hidden 
                  transform hover:scale-105 hover:shadow-2xl transition-all duration-300
                  ${isDarkMode 
                    ? 'bg-gray-800 border border-gray-700 hover:border-[#1E9B4C] shadow-gray-900/50' 
                    : 'bg-white border border-gray-100 hover:border-[#1E9B4C] shadow-gray-300/20'
                  }
                `}
              >
                <div
                  className={`w-16 h-16 rounded-lg flex items-center justify-center text-white mb-4
                    ${feature.color || 'bg-[#1E9B4C]'}
                  `}
                >
                  {feature.icon}
                </div>

                <h3
                  className={`text-xl font-bold mb-3 transition-colors ${
                    isDarkMode
                      ? 'text-white group-hover:text-[#1E9B4C]'
                      : 'text-gray-900 group-hover:text-[#1E9B4C]'
                  }`}
                >
                  {feature.title}
                </h3>

                <p
                  className={`transition-colors ${
                    isDarkMode 
                      ? 'text-gray-300 group-hover:text-gray-200' 
                      : 'text-gray-600 group-hover:text-gray-700'
                  }`}
                >
                  {feature.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Use Cases Section */}
      <section className={`py-16 px-4 transition-colors duration-300 ${
        isDarkMode ? 'bg-gray-900' : 'bg-gray-50'
      }`}>
        <div className="container mx-auto">
          <div className="text-center mb-12">
            <h2
              className={`text-3xl font-bold mb-4 ${
                isDarkMode ? 'text-white' : 'text-gray-900'
              }`}
            >
              Berbagai Aplikasi dan Penggunaan
            </h2>
            <p
              className={`text-lg ${
                isDarkMode ? 'text-gray-300' : 'text-gray-600'
              }`}
            >
              Solusi tepat untuk berbagai kebutuhan komunikasi audio
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {useCases.map((useCase, index) => (
              <div
                key={index}
                className={`rounded-xl overflow-hidden shadow-lg hover:shadow-xl transition-all duration-300 transform hover:scale-105 ${
                  isDarkMode 
                    ? 'bg-gray-800 border border-gray-700 shadow-gray-900/50' 
                    : 'bg-white border border-gray-100 shadow-gray-300/20'
                }`}
              >
                <div className="relative">
                  <img
                    src={useCase.image}
                    alt={useCase.title}
                    className="w-full h-48 object-cover"
                  />
                  <div className={`absolute inset-0 ${
                    isDarkMode 
                      ? 'bg-gradient-to-t from-gray-800/20 to-transparent' 
                      : 'bg-gradient-to-t from-white/20 to-transparent'
                  }`}></div>
                </div>
                <div className="p-6">
                  <h3
                    className={`text-xl font-bold mb-3 ${
                      isDarkMode ? 'text-white' : 'text-gray-900'
                    }`}
                  >
                    {useCase.title}
                  </h3>
                  <p
                    className={`mb-4 ${
                      isDarkMode ? 'text-gray-300' : 'text-gray-600'
                    }`}
                  >
                    {useCase.description}
                  </p>
                  <div className="space-y-2">
                    {useCase.benefits.map((benefit, idx) => (
                      <div key={idx} className="flex items-center gap-2">
                        <CheckCircle className="w-4 h-4 text-[#1E9B4C] flex-shrink-0" />
                        <span className={`text-sm ${
                          isDarkMode ? 'text-gray-400' : 'text-gray-600'
                        }`}>
                          {benefit}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Implementation Process */}
      <section className={`py-16 px-4 transition-colors duration-300 ${
        isDarkMode ? 'bg-gray-800' : 'bg-white'
      }`}>
        <div className="container mx-auto">
          <div className="text-center mb-12">
            <h2
              className={`text-3xl font-bold mb-4 ${
                isDarkMode ? 'text-white' : 'text-gray-900'
              }`}
            >
              Proses Implementasi
            </h2>
            <p
              className={`text-lg ${
                isDarkMode ? 'text-gray-300' : 'text-gray-600'
              }`}
            >
              Langkah-langkah profesional untuk hasil terbaik
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {implementationSteps.map((step, index) => (
              <div
                key={index}
                className={`p-6 rounded-xl border-2 transition-all duration-300 hover:border-[#1E9B4C] hover:shadow-lg transform hover:scale-105 ${
                  isDarkMode 
                    ? 'bg-gray-800 border-gray-700 shadow-gray-900/50 hover:shadow-xl hover:bg-gray-750' 
                    : 'bg-gray-50 border-gray-200 shadow-gray-300/20 hover:shadow-xl hover:bg-white'
                }`}
              >
                <div className="flex items-center gap-4 mb-4">
                  <div className="w-12 h-12 rounded-full bg-[#1E9B4C] flex items-center justify-center text-white font-bold text-xl">
                    {step.step}
                  </div>
                  <h3 className={`font-bold text-lg ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                    {step.title}
                  </h3>
                </div>
                <p className={`${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>
                  {step.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

     <Diskon />

      {/* CTA Section */}
      <section
        className={`py-16 px-4 transition-colors duration-300 ${
          isDarkMode ? 'bg-gray-800' : 'bg-[#1E9B4C]'
        }`}
      >
        <div className="container mx-auto text-center">
          <h2 className="text-3xl font-bold text-white mb-4">
            Siap Tingkatkan Sistem Komunikasi Anda?
          </h2>
          <p className="text-xl text-green-100 mb-8 max-w-2xl mx-auto">
            Dapatkan konsultasi gratis dari ahli kami untuk solusi IP Speaker System yang tepat untuk kebutuhan Anda.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <button 
              onClick={() => contactViaWhatsApp("IP Speaker System")}
              className="bg-white text-[#1E9B4C] hover:bg-gray-100 font-semibold px-8 py-4 rounded-lg transition-colors duration-200 flex items-center gap-2 justify-center"
            >
              <MessageCircle className="w-5 h-5" />
              Konsultasi Gratis
            </button>
            <button 
              onClick={() => contactViaWhatsApp("IP Speaker Enterprise")}
              className="border-2 border-white text-white hover:bg-white hover:text-[#1E9B4C] font-semibold px-8 py-4 rounded-lg transition-colors duration-200 flex items-center gap-2 justify-center"
            >
              <ArrowRight className="w-5 h-5" />
              Hubungi Sales
            </button>
          </div>
        </div>
      </section>

      {/* Footer */}
      <Footer />
    </div>
  );
};

export default IPSpeakerPage;